import { formatMoney } from '@theme/money-formatting';

/**
 * "Consultar frete e prazo" na PDP.
 *
 * A Shopify só cota frete para o carrinho, então a cotação é feita assim:
 * 1. adiciona a variante/quantidade selecionada ao carrinho (POST /cart/add.js);
 * 2. cota pela API de frete do carrinho (mesmas opções/prazos do checkout, ex.: Boxlink):
 *    POST /cart/prepare_shipping_rates.json → GET /cart/async_shipping_rates.json (polling);
 * 3. devolve a linha à quantidade original (POST /cart/change.js) — sempre, mesmo se a cotação falhar.
 *
 * Para encurtar a espera: o ViaCEP é consultado assim que o CEP fica completo (antes do clique) e roda em
 * paralelo com o add; a quantidade original vem da própria resposta do add (sem GET /cart.js); o polling
 * começa curto e vai espaçando; e a restauração do carrinho não segura a exibição do resultado.
 *
 * As chamadas são feitas direto, sem disparar os eventos de carrinho do tema, então o minicart
 * não abre e o contador do carrinho não muda. Se o carrinho já tiver itens, a cotação considera
 * esses itens junto com o produto (é o que o checkout cobraria).
 */

const STORAGE_KEY = 'pasquini:shipping-consult-cep';
/** Intervalos do polling (ms): começa curto porque a cotação costuma ficar pronta em ~1s. */
const POLL_DELAYS = [250, 250, 400, 400, 600, 600, 800, 800, 1000, 1000, 1000, 1000, 1000, 1000];

/** @type {Map<string, Promise<string | null>>} */
const provinceCache = new Map();

class ProductShippingConsult extends HTMLElement {
  /** @type {boolean} */
  #busy = false;

  /** Restauração do carrinho da cotação anterior; a próxima cotação espera ela terminar. */
  /** @type {Promise<void> | null} */
  #pendingRestore = null;

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('input', this.#onInput);

    const savedCep = this.#savedCep;
    if (savedCep && this.#input && !this.#input.value) this.#input.value = formatCep(savedCep);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('input', this.#onInput);
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="cep"]'));
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('button[type="submit"]'));
  }

  get #results() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-shipping-results]'));
  }

  /** Mesmo CEP salvo pelo minicart (assets/cart-shipping-consult.js). */
  get #savedCep() {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** @param {string} cep */
  set #savedCep(cep) {
    try {
      sessionStorage.setItem(STORAGE_KEY, cep);
    } catch {}
  }

  /** Variante e quantidade escolhidas no formulário de compra da mesma seção. */
  get #selection() {
    const scope = this.closest('.shopify-section') || document;
    const variantInput = /** @type {HTMLInputElement | null} */ (
      scope.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')
    );
    const quantityInput = /** @type {HTMLInputElement | null} */ (scope.querySelector('input[name="quantity"]'));

    return {
      variantId: Number(variantInput?.value || this.dataset.variantId || 0),
      quantity: Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1),
    };
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const input = this.#input;
    if (event.target !== input || !input) return;
    input.value = formatCep(input.value);

    // Adianta a consulta do estado enquanto o cliente ainda vai clicar em "Consultar".
    const cep = input.value.replace(/\D/g, '');
    if (cep.length === 8) fetchProvince(cep).catch(() => {});
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    event.preventDefault();
    if (this.#busy) return;
    const cep = (this.#input?.value || '').replace(/\D/g, '');
    this.#quote(cep);
  };

  /** @param {string} cep - 8 digits */
  async #quote(cep) {
    if (cep.length !== 8) {
      this.#renderMessage('Digite um CEP válido.', true);
      return;
    }

    const { variantId, quantity } = this.#selection;
    if (!variantId) {
      this.#renderMessage('Selecione uma variação do produto.', true);
      return;
    }

    this.#busy = true;
    this.#setLoading(true);
    this.#renderMessage('Calculando frete…');

    /** @type {{ key: string, quantity: number } | null} */
    let restore = null;

    try {
      if (this.#pendingRestore) await this.#pendingRestore;

      // Estado (ViaCEP) e add ao carrinho em paralelo.
      const provincePromise = fetchProvince(cep);
      provincePromise.catch(() => {});

      try {
        const added = await fetchJson(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        });

        // O add devolve a linha com a quantidade total: a original é essa menos o que foi somado.
        const line = added?.items?.[0];
        if (line?.key) restore = { key: line.key, quantity: Math.max(0, (line.quantity || 0) - quantity) };
      } catch (error) {
        if (!(error instanceof ShippingError)) throw error;
        // Sem estoque para somar mais unidades, mas o produto já está no carrinho: cota o carrinho como está.
        const cart = await fetchJson(`${Theme.routes.cart_url}.js`);
        const inCart = cart?.items?.some((/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId);
        if (!inCart) throw error;
      }

      const province = await provincePromise;
      const params = new URLSearchParams({
        'shipping_address[zip]': formatCep(cep),
        'shipping_address[country]': 'Brazil',
      });
      if (province) params.set('shipping_address[province]', province);

      const prepare = await fetch(`${Theme.routes.cart_url}/prepare_shipping_rates.json?${params}`, {
        method: 'POST',
      });
      if (!prepare.ok) {
        const errors = await prepare.json().catch(() => null);
        throw new ShippingError(firstError(errors));
      }

      const rates = await pollRates(params);
      this.#savedCep = cep;
      this.#renderRates(rates);
    } catch (error) {
      const message =
        error instanceof ShippingError && error.message
          ? error.message
          : 'Não foi possível calcular o frete. Tente novamente.';
      this.#renderMessage(message, true);
    } finally {
      // O resultado já está na tela; a restauração segue em segundo plano.
      this.#busy = false;
      this.#setLoading(false);
      if (restore) {
        const pending = restoreLine(restore).finally(() => {
          if (this.#pendingRestore === pending) this.#pendingRestore = null;
        });
        this.#pendingRestore = pending;
      }
    }
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    const button = this.#button;
    if (!button) return;
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #renderMessage(message, isError = false) {
    const results = this.#results;
    if (!results) return;

    const paragraph = document.createElement('p');
    paragraph.className = `product-shipping-consult__message${isError ? ' product-shipping-consult__message--error' : ''}`;
    paragraph.textContent = message;

    results.replaceChildren(paragraph);
    results.hidden = false;
  }

  /** @param {ShippingRate[]} rates */
  #renderRates(rates) {
    const results = this.#results;
    if (!results) return;

    if (!rates.length) {
      this.#renderMessage('Nenhuma opção de entrega disponível para este CEP.', true);
      return;
    }

    const list = document.createElement('ul');
    list.className = 'product-shipping-consult__rates';
    list.setAttribute('role', 'list');

    for (const rate of [...rates].sort((a, b) => Number(a.price) - Number(b.price))) {
      const item = document.createElement('li');
      item.className = 'product-shipping-consult__rate';

      const info = document.createElement('span');
      info.className = 'product-shipping-consult__rate-info';

      const name = document.createElement('span');
      name.className = 'product-shipping-consult__rate-name';
      name.textContent = rate.presentment_name || rate.name;
      info.append(name);

      const deliveryText = formatDelivery(rate.delivery_days);
      if (deliveryText) {
        const delivery = document.createElement('span');
        delivery.className = 'product-shipping-consult__rate-delivery';
        delivery.textContent = deliveryText;
        info.append(delivery);
      }

      const price = document.createElement('span');
      price.className = 'product-shipping-consult__rate-price';
      const cents = Math.round(Number(rate.price) * 100);
      price.textContent =
        cents === 0 ? 'Grátis' : formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', rate.currency || 'BRL');

      item.append(info, price);
      list.append(item);
    }

    results.replaceChildren(list);
    results.hidden = false;
  }
}

/**
 * @typedef {{ name: string, presentment_name?: string, price: string, currency?: string, delivery_days?: number[] }} ShippingRate
 */

class ShippingError extends Error {}

/**
 * @param {string} url
 * @param {RequestInit} [init]
 */
async function fetchJson(url, init) {
  const response = await fetch(url, init);
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    // /cart/add.js responde 422 quando não há estoque suficiente
    if (response.status === 422) throw new ShippingError(body?.description || 'Produto indisponível na quantidade selecionada.');
    throw new Error(`${url} ${response.status}`);
  }
  return body;
}

/**
 * Devolve a linha à quantidade original (0 remove). Tenta de novo uma vez se falhar.
 * @param {{ key: string, quantity: number }} line
 */
async function restoreLine({ key, quantity }) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (response.ok) return;
    } catch {}
  }
  console.warn('[product-shipping-consult] Não foi possível restaurar o carrinho após a cotação.');
}

/** @param {string} value */
function formatCep(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

/** @param {number[] | undefined} days */
function formatDelivery(days) {
  if (!Array.isArray(days) || !days.length) return '';
  const [min, max = min] = days;
  if (!max) return '';
  return min === max ? `Até ${max} dias úteis` : `${min} a ${max} dias úteis`;
}

/**
 * Shopify's shipping zones for Brazil are per state, so resolve it from the CEP.
 * Cached per CEP so the lookup started while typing is reused on submit.
 * @param {string} cep
 * @returns {Promise<string | null>}
 */
function fetchProvince(cep) {
  let promise = provinceCache.get(cep);
  if (!promise) {
    promise = lookupProvince(cep);
    provinceCache.set(cep, promise);
    // Falha de rede (null) não fica em cache; "CEP não encontrado" fica.
    promise.then((province) => province === null && provinceCache.delete(cep)).catch(() => {});
  }
  return promise;
}

/**
 * @param {string} cep
 * @returns {Promise<string | null>}
 */
async function lookupProvince(cep) {
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();
    if (data?.erro) throw new ShippingError('CEP não encontrado.');
    return data?.estado || data?.uf || null;
  } catch (error) {
    if (error instanceof ShippingError) throw error;
    // ViaCEP fora do ar: tenta só com o CEP
    return null;
  }
}

/**
 * @param {URLSearchParams} params
 * @returns {Promise<ShippingRate[]>}
 */
async function pollRates(params) {
  for (const delay of [0, ...POLL_DELAYS]) {
    if (delay) await new Promise((resolve) => setTimeout(resolve, delay));

    const response = await fetch(`${Theme.routes.cart_url}/async_shipping_rates.json?${params}`);

    if (response.status === 200) {
      const data = await response.json();
      if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
    } else if (response.status !== 202) {
      const errors = await response.json().catch(() => null);
      throw new ShippingError(firstError(errors));
    }
  }

  throw new Error('Shipping rates timeout');
}

/**
 * Shopify returns errors as `{ field: ["message"] }`, in English — map to a PT-BR message instead.
 * @param {any} errors
 * @returns {string}
 */
function firstError(errors) {
  if (!errors || typeof errors !== 'object') return '';
  if ('zip' in errors) return 'CEP inválido ou sem entrega disponível.';
  return '';
}

if (!customElements.get('product-shipping-consult')) {
  customElements.define('product-shipping-consult', ProductShippingConsult);
}
